/**
 * Disturbance tuning — one knob per visual property, all independent.
 *
 * These values are identical in the home field and the details field in the
 * source prototypes, which is what makes the two screens read as the same
 * substance. They are extracted here so they cannot drift apart. Previous
 * values are kept in the comments so any single change can be reverted alone.
 *
 * Changing anything under "seamless motion" below means re-running the
 * continuity test in HANDOFF §5.
 */

/**
 * Global time scale for the fronts and their precession. 1.00 = the earlier
 * build. All three frequencies are scaled by the SAME factor, so their mutual
 * ratios — and therefore the non-recurrence property — are untouched; only the
 * wall clock stretches. Sweep periods go 13.7 / 21.1 / 8.9 s → 40 / 62 / 26 s.
 */
export const SLOW = 0.34;

/** Half-width of the bright acquisition band, in field radii. Was 0.030. */
export const BAND = 0.105;
/** Base half-width of the trailing glow. Was 0.085. */
export const GLOW_W = 0.155;
/** Extra glow width on the just-crossed side. Was 0.255. */
export const GLOW_S = 0.33;
/**
 * Per-point offset of the band surface, in field radii. Comparable to BAND, so
 * the band stops being a mathematical plane and becomes a ragged shell. This is
 * what breaks the line; 0 restores the old hard plane.
 */
export const SCAT = 0.08;
/** Depth of per-point scintillation, 0…1. Band term only, so dust outside the sweep never twinkles. */
export const TWK = 0.5;
/** Scintillation rate multiplier; per-point periods ~7–20 s. */
export const TWK_HZ = 0.6;
/**
 * Uniform scale on EVERY band gain. Needed because BAND is 3.5× wider and
 * therefore lights ~3.5× as many points at once; without it the frame washes
 * out. Preserves the ratios between layers.
 */
export const EDGE_K = 0.52;
/** Same idea for the glow gains. */
export const GLOW_K = 0.82;
/** Radial + normal scatter on the drawn cut rings, so they read as the core of a soft band. */
export const CUT_SCAT = 0.055;

export interface Front {
  /** Frequency, already scaled by SLOW. */
  f: number;
  /** Phase offset. */
  ph: number;
  /** Precession rate, already scaled by SLOW. */
  pr: number;
  /** Axis seed. */
  ax: number;
  /** Amplitude of the sweep, in field radii. */
  amp: number;
  /** Jitter weight: decorrelates each point's offset between fronts at zero extra hashing cost. */
  jw: number;
}

/**
 * Three acquisition fronts.
 *
 * Frequencies and precession rates are mutually incommensurate, so the
 * superposition never returns to the same state — there is no period to
 * perceive, hence no start and no end. Verified: over two hours of runtime the
 * composite state never comes within 0.31 (on −1…1) of the opening frame.
 */
export const FRONTS: Front[] = [
  { f: 0.0731 * SLOW, ph: 0.0, pr: 0.0193 * SLOW, ax: 0.0, amp: 1.03, jw: 1.0 },
  {
    f: 0.0473 * SLOW,
    ph: 2.31,
    pr: 0.0271 * SLOW,
    ax: 2.09,
    amp: 0.97,
    jw: -0.82,
  },
  {
    f: 0.1117 * SLOW,
    ph: 4.11,
    pr: 0.0119 * SLOW,
    ax: 4.19,
    amp: 0.88,
    jw: 0.61,
  },
];

/**
 * Cheap smooth falloff, replacing exp() so ~4400 evaluations per frame stay fast.
 * Shape matters more than the exact profile here; what matters is that it is C¹.
 */
export function fall(d: number, w: number): number {
  const x = d / w;
  return 1 / (1 + x * x * 3.0);
}

/** Per-frame state of one front. */
export interface FrontState {
  n: [number, number, number];
  /** Signed position along its normal, in field radii. */
  p: number;
  /** Velocity sign tells us which side was just crossed. */
  v: number;
  jw: number;
}

/** Evaluate all three fronts for time `t`. Computed once per frame, reused by every point. */
export function frontStates(t: number): FrontState[] {
  const out: FrontState[] = [];
  for (const fr of FRONTS) {
    const pos = fr.amp * Math.sin(t * fr.f * 6.2832 + fr.ph); // smooth reversal: no seam
    const vel = Math.cos(t * fr.f * 6.2832 + fr.ph);
    const a1 = fr.ax + t * fr.pr * 6.2832;
    const a2 = fr.ax * 0.7 + t * fr.pr * 3.9;
    const nx = Math.sin(a2) * Math.cos(a1);
    const ny = Math.cos(a2);
    const nz = Math.sin(a2) * Math.sin(a1);
    out.push({ n: [nx, ny, nz], p: pos, v: vel, jw: fr.jw });
  }
  return out;
}

/**
 * Light at a normalised 3D position. Returns `[glow, band]`.
 *
 * `jit` is this point's offset of the band surface (see SCAT), passed in rather
 * than hashed here so it costs one hash per point instead of three.
 *
 * The tanh is not decoration. A hard `behind / ahead` boolean pops when the
 * front's velocity crosses zero at each turnaround — measured as a 0.71
 * brightness jump — so the two kernels are blended smoothly instead. `fr.v` has
 * unit amplitude regardless of SLOW, so this gain does not need rescaling when
 * the fronts slow down.
 */
export function lightAt(
  F: FrontState[],
  x: number,
  y: number,
  z: number,
  jit: number,
): [number, number] {
  let best = 0;
  let edge = 0;
  for (const fr of F) {
    const d = x * fr.n[0] + y * fr.n[1] + z * fr.n[2] - fr.p + jit * fr.jw;
    const mix = 0.5 + 0.5 * Math.tanh(-d * fr.v * 7.0);
    const gl = fall(d, GLOW_W + GLOW_S * mix) * (0.42 + 0.58 * mix);
    if (gl > best) best = gl;
    const e = fall(d, BAND); // the bright acquisition line itself
    if (e > edge) edge = e;
  }
  return [best, edge];
}
